import { nanoid } from 'nanoid'

/* @ngInject */
function UploadTabController (
  $scope,
  $rootScope,
  $timeout,
  $state,
  $translate,
  $q,
  UploadService,
  ToastService,
  Utils,
  UdsModalService,
  NewAssetService,
  RepositoryService,
  AssetService,
  SegmentService
) {
  const getMoveToTrashDialogController = () =>
    $q.when(import('../../dialogs/move-to-trash/move-to-trash-dialog.controller'))
  const getManageAssetsDialogController = () =>
    $q.when(import('../../dialogs/manage-assets/manage-assets-dialog.controller'))
  const getOrganizeAssetDialogController = () =>
    $q.when(import('../../dialogs/organize-asset/organize-asset-dialog.controller'))

  const fileProgress = {}

  $scope.disableConfigButton = false
  $scope.dropdownDirection = 'down'
  $scope.showUploadTab = false
  $scope.isMaterialLoading = true
  $scope.minimized = false
  $scope.minimizeIcon = 'chevron-down'
  $scope.progress = 0
  $scope.materials = []
  $scope.uploadedFiles = []
  $scope.isUploading = false
  $scope.pricing = {
    organized: {
      access: false,
      pricingPlan: 'Pro'
    }
  }

  const api = {
    _isAnyMaterialLoading,
    _uploadToS3,
    _handleSuccessUploadAssets,
    _handleErrorUploadAssets,
    _setMaterialsArray,
    getOrganizeAssetDialogController,
    getManageAssetsDialogController,
    getMoveToTrashDialogController,
    _setTabTitle,
    _updateFileProgress,
    _updateTotalProgress
  }

  this.getApi = () => api

  function activate () {
    api._isAnyMaterialLoading($scope.materials)
    api._setTabTitle()

    RepositoryService.hasOrganizedAccess().then(hasAccess => {
      $scope.pricing.organized.access = hasAccess
    })
  }

  activate()

  const deregisterUploadAssetsStatus = $rootScope.$on('upload-tab.upload-new-assets', (e, assets, options = {}) => {
    if (!assets.length) {
      return
    }

    const validatedFiles = assets.map(asset => {
      if (asset.type) {
        return Promise.resolve(asset)
      }

      return Utils.fileIsFolder(asset).catch(() => {
        return null
      })
    })

    Promise.all(validatedFiles).then(files => {
      let hasBlockedFile = false
      const validFiles = files.filter(file => {
        if (!file) {
          return false
        }

        const blockedFileTypes = ['.vbs', '.exe']
        const fileExtension = Utils.getFileExtensionByName(file.name)
        const isBlocked = blockedFileTypes.includes(fileExtension)

        if (isBlocked) {
          hasBlockedFile = isBlocked
        }

        return !isBlocked
      })

      if (hasBlockedFile) {
        ToastService.showErrorToast(
          $translate.instant('repository.attachInsideTheModuleModal.toast.cannotUploadVbsAndExe')
        )
      }

      if (!validFiles.length) {
        return
      }

      $scope.showUploadTab = true
      api._setMaterialsArray(validFiles)
      api._uploadToS3(validFiles, options)
    })
  })

  const deregisterHandleSaveAssets = $rootScope.$on('repository.update-assets', (e, updatedAsset) => {
    for (const material of $scope.materials) {
      material.name = updatedAsset.name
    }
    for (const uploadedFile of $scope.uploadedFiles) {
      uploadedFile.name = updatedAsset.name
    }
  })

  $scope.$on('$destroy', () => {
    deregisterUploadAssetsStatus()
    deregisterHandleSaveAssets()
  })

  function _uploadToS3 (files, options = {}) {
    const { folderId, isOrganized = false, campaignId } = options
    $scope.isUploading = true
    $scope.campaignId = campaignId

    files.forEach(file => {
      if (!fileProgress[file.uniqueId]) {
        fileProgress[file.uniqueId] = 0
      }
    })

    return UploadService.uploadMultipleFileToS3(files, {
      onUploadProgress: (file, progress) => {
        api._updateFileProgress(fileProgress, file, progress)
      },
      asset: {
        folderId,
        isOrganized
      }
    })
      .then(uploadedFiles => {
        files.forEach(file => {
          api._updateFileProgress(fileProgress, file, 100)
        })

        api._handleSuccessUploadAssets(uploadedFiles, campaignId)
      })
      .catch(error => {
        files.forEach(file => {
          if (fileProgress[file.uniqueId] < 100) {
            const uploadFile = $scope.materials.find(material => material.uniqueId === file.uniqueId)

            $timeout(() => {
              uploadFile.errorUploading = true
            })
          }
        })
        api._handleErrorUploadAssets()
        console.log(error)
      })
  }

  function _updateFileProgress (fileProgress, file, progress) {
    fileProgress[file.uniqueId] = progress
    api._updateTotalProgress(fileProgress)

    if (progress === 100) {
      const uploadFile = $scope.materials.find(material => material.uniqueId === file.uniqueId)
      uploadFile.isUploading = false
    }
  }

  function _updateTotalProgress (fileProgress) {
    api._setTabTitle('uploading')
    const totalProgress = Object.values(fileProgress).reduce((sum, progress) => sum + progress, 0)

    $timeout(() => {
      $scope.progress = (totalProgress / Object.keys(fileProgress).length).toFixed(2)
    })
  }

  function _setMaterialsArray (assets) {
    const mappedAssets = assets.map(asset => {
      const uniqueId = nanoid()
      asset.uniqueId = uniqueId
      const nameWithoutExtension = Utils.removeExtensionFromFileName(asset.name)

      return {
        uniqueId,
        name: nameWithoutExtension,
        size: asset.size,
        type:
          nameWithoutExtension.length < 12
            ? Utils.getFileExtensionByName(asset.name)
            : ` ${Utils.getFileExtensionByName(asset.name)}`,
        assetId: asset.id,
        isUploading: true,
        errorUploading: false
      }
    })
    $scope.materials.unshift(...mappedAssets)
  }

  function _handleSuccessUploadAssets (lastUploadedFiles, campaignId) {
    $scope.uploadedFiles = $scope.uploadedFiles.concat(lastUploadedFiles)
    $scope.isUploading = false
    ToastService.showSuccessToast($translate.instant('repository.attachInsideTheModuleModal.toast.successUploadAssets'))
    if (campaignId) {
      $rootScope.$broadcast('campaign.add-attachments-to-campaign', $scope.uploadedFiles)
    } else {
      $rootScope.$broadcast('reload-assets-after-upload', $scope.uploadedFiles)
    }
    $scope.progress = 0

    lastUploadedFiles.forEach(uploadedFile => {
      SegmentService.track('uploaded-file', { asset_id: uploadedFile.id })
    })
  }

  function _handleErrorUploadAssets () {
    ToastService.showErrorToast($translate.instant('repository.attachInsideTheModuleModal.toast.errorUploadAssets'))
    api._setTabTitle('error')
  }

  function _isAnyMaterialLoading (materials) {
    $scope.isMaterialLoading = materials.some(material => material.isUploading === true)
    if ($scope.isMaterialLoading) {
      api._setTabTitle('uploading')
    } else {
      api._setTabTitle()
    }
  }

  function _setTabTitle (state) {
    const standard =
      $scope.materials.length > 1
        ? $translate.instant('uploadTab.titles.standard', { materialsLength: $scope.materials.length })
        : $translate.instant('uploadTab.titles.standardSingular', { materialsLength: $scope.materials.length })

    if (state === 'uploading') {
      $scope.uploadTabTitle =
        standard +
        $translate.instant('uploadTab.titles.uploading', {
          materialsLength: $scope.materials.length,
          uploadingPercentage: $scope.progress
        })
    } else if (state === 'error') {
      $scope.uploadTabTitle = standard + $scope.progress + '%'
    } else {
      $scope.uploadTabTitle = standard
    }
  }

  $scope.$watch(
    'materials',
    function (newVal, oldVal) {
      if (newVal !== oldVal) {
        api._isAnyMaterialLoading($scope.materials)
      }
    },
    true
  )

  $scope.minimize = function () {
    $scope.minimized = !$scope.minimized
    if ($scope.minimized) {
      $scope.minimizeIcon = 'chevron-up'
      $scope.dropdownDirection = 'up'
    } else {
      $scope.minimizeIcon = 'chevron-down'
      $scope.dropdownDirection = 'down'
    }
  }

  $scope.closeModal = function () {
    $scope.showUploadTab = false
    $scope.materials = []
    $scope.uploadedFiles = []
  }

  $scope.copyAssetsLinks = function (assets) {
    NewAssetService.copyAssetsUrlsToClipboard(assets)
      .then(() => {
        ToastService.showSuccessToast($translate.instant('repository.toasts.copyLinkBatch.success'))
      })
      .catch(() => {
        ToastService.showErrorToast($translate.instant('repository.toasts.copyLinkBatch.error'))
      })
  }

  $scope.downloadUploadedAssets = assets => {
    if (assets.length > 1) {
      return NewAssetService.downloadAssetsBatch(assets, 'REPOSITORY')
    }

    return AssetService.downloadAsset(assets[0].id)
  }

  $scope.organizeAsset = files => {
    const nameDisplayLength = 32
    const assets = files.map(file => {
      return {
        id: file.id,
        assetName: Utils.truncateText(file?.files?.[0]?.name, nameDisplayLength),
        folderId: file.folderId,
        isFolder: false,
        isOrganized: file.isOrganized
      }
    })

    api.getOrganizeAssetDialogController().then(({ OrganizeAssetController }) => {
      const organizeAssetDialog = UdsModalService.createModal({
        controller: OrganizeAssetController,
        modalName: 'organizeAssetDialog',
        data: {
          assets,
          isRestoring: false,
          hasOrganizedFeature: $scope.pricing.organized.access
        },
        onClose: ({ hasMoved, targetFolderId }) => {
          if (hasMoved) {
            $scope.uploadedFiles.forEach(file => {
              file.folderId = targetFolderId
              file.isOrganized = true
            })

            if ($state.current.name.includes('organized')) {
              // we use this event to reload assets so the user doesn't need to reload the page in order to see the changes
              $rootScope.$emit('reload-assets-after-upload', $scope.uploadedFiles)
            }
          }
        }
      })

      organizeAssetDialog.open()
    })
  }

  $scope.archiveSelectedAssets = uploadedAssets => {
    const nameDisplayLength = 32
    const assets = uploadedAssets.map(uploadedAsset => {
      return {
        id: uploadedAsset.id,
        assetSize: Utils.getReadableFileSizeString(uploadedAsset.files[0].size),
        assetName: RepositoryService.defineFullAssetNameToDisplay(uploadedAsset.files[0], nameDisplayLength),
        assetIcon: RepositoryService.defineAssetIconBasedOnExtension(uploadedAsset.files[0])
      }
    })

    api.getMoveToTrashDialogController().then(({ MoveToTrashController }) => {
      const moveToTrashDialog = UdsModalService.createModal({
        controller: MoveToTrashController,
        modalName: 'moveToTrashDialog',
        data: {
          assets,
          selectedAssets: $scope.uploadedFiles
        },
        onClose: hasMoved => {
          if (hasMoved) {
            $rootScope.$broadcast('repository.assets-archive', $scope.uploadedFiles)
            $scope.closeModal()
          }
        }
      })
      moveToTrashDialog.open()
    })
  }

  $scope.openSettingsBatch = (selectedAssets, tabToOpen) => {
    $scope.disableConfigButton = true

    getManageAssetsDialogController().then(({ ManageAssetsDialogController }) => {
      const manageAssetsDialog = UdsModalService.createModal({
        controller: ManageAssetsDialogController,
        modalName: 'manageAssetsDialog',
        data: {
          assets: selectedAssets,
          tabToOpen
        },
        onClose: updatedAsset => {
          if (!updatedAsset) return

          const assetNameWasUpdated = updatedAsset.name !== selectedAssets[0].name

          if (assetNameWasUpdated) {
            $rootScope.$emit('asset-card.update-asset-name', updatedAsset.id, updatedAsset.name)
          }

          $scope.disableConfigButton = false
        }
      })

      manageAssetsDialog.open()
    })
  }
}

export { UploadTabController }
